import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { Loader2, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DatePicker } from '@/components/custom/date-picker';
import { useAppointmentTypes } from '@/hooks/use-appointment-types';
import { useCreateTestBooking } from '@/hooks/use-test-booking';
import {
  testBookingDefaults,
  testBookingFormSchema,
  type TestBookingFormValues,
} from './test-booking-form';

/**
 * Add a test appointment for a patient (ADR-293). Platform admins in a demo
 * practice only; the caller decides whether to mount the button that opens it.
 * The server picks any provider and location, makes availability when none is
 * open, and never sends the appointment to the EHR.
 */
export function TestBookingDialog({
  patientId,
  open,
  onOpenChange,
}: {
  patientId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.ReactElement {
  const { data: appointmentTypes, isLoading: typesLoading } = useAppointmentTypes();
  const createTestBooking = useCreateTestBooking();

  const form = useForm<TestBookingFormValues>({
    resolver: zodResolver(testBookingFormSchema),
    defaultValues: testBookingDefaults(),
  });
  const { errors } = form.formState;
  const busy = createTestBooking.isPending;

  const close = (): void => {
    if (busy) return;
    form.reset(testBookingDefaults());
    onOpenChange(false);
  };

  const submit = form.handleSubmit(async (values) => {
    try {
      const { appointment } = await createTestBooking.mutateAsync({ patientId, ...values });
      toast.success(`Test appointment added. Confirmation ${appointment.confirmationNumber}`);
      close();
    } catch {
      // The global mutation toast has already shown why; keep the dialog open to retry.
    }
  });

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="size-4" />
            Add an appointment (for TESTING ONLY)
          </DialogTitle>
          <DialogDescription>
            Saved in VagusX only and never sent to the EHR. Any provider and location; if nobody has
            an open slot then, one is made for the first free provider.
          </DialogDescription>
        </DialogHeader>

        <form id="test-booking-form" onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="test-booking-type">Appointment type</Label>
            <Controller
              control={form.control}
              name="appointmentTypeId"
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange} disabled={typesLoading}>
                  <SelectTrigger id="test-booking-type" className="w-full">
                    <SelectValue placeholder={typesLoading ? 'Loading…' : 'Pick a type'} />
                  </SelectTrigger>
                  <SelectContent>
                    {(appointmentTypes ?? []).map((type) => (
                      <SelectItem key={type.id} value={type.id}>
                        {type.name} ({type.durationMinutes} min)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {errors.appointmentTypeId && (
              <p className="text-sm text-destructive">{errors.appointmentTypeId.message}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="test-booking-date">Date</Label>
              <Controller
                control={form.control}
                name="date"
                render={({ field }) => (
                  <DatePicker
                    id="test-booking-date"
                    value={field.value}
                    onChange={field.onChange}
                    className="w-full"
                  />
                )}
              />
              {errors.date && <p className="text-sm text-destructive">{errors.date.message}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="test-booking-time">Time (clinic local)</Label>
              <Input id="test-booking-time" type="time" step={300} {...form.register('time')} />
              {errors.time && <p className="text-sm text-destructive">{errors.time.message}</p>}
            </div>
          </div>
        </form>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={close} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" form="test-booking-form" disabled={busy}>
            {busy && <Loader2 className="size-4 animate-spin" />}
            Add test appointment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
